import { useState } from 'react';
import { Button, Field, Input, useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useSaveDoctorDetails } from '@/features/doctor-profiles/api';
import type { DoctorProfile } from '@/features/doctor-profiles/types';
import { isApiError } from '@/lib/api';
import parity from '@/pages/ops/parity.module.css';

const MMC = /^[A-Za-z0-9-]{3,20}$/;

/**
 * RL-1791 (decision D25): the clinic manager keeps a doctor's name, sign-in
 * email, MMC number and APC expiry up to date on Doctors & rosters. The MMC
 * and a current APC are needed to sign prescriptions and issue MCs.
 */
export function DoctorDetailsSection({
  staffId,
  profile: p,
  canEdit,
}: {
  staffId: string;
  profile: DoctorProfile;
  canEdit: boolean;
}) {
  const toast = useToast();
  const { hasPermission } = useSession();
  // Review: only the Super admin changes a sign-in email (API RL-1790).
  const canEmail = hasPermission('staff:manage');
  const save = useSaveDoctorDetails(staffId);
  const [form, setForm] = useState({
    fullName: p.name,
    email: p.email ?? '',
    mmcNumber: p.mmcNumber ?? '',
    apcValidUntil: p.apcValidUntil ?? '',
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const mmcBad =
    form.mmcNumber.trim() !== '' && !MMC.test(form.mmcNumber.trim());
  const changed = {
    ...(form.fullName.trim() !== p.name
      ? { fullName: form.fullName.trim() }
      : {}),
    ...(canEmail && form.email.trim() && form.email.trim() !== (p.email ?? '')
      ? { email: form.email.trim() }
      : {}),
    ...(form.mmcNumber.trim() !== (p.mmcNumber ?? '')
      ? { mmcNumber: form.mmcNumber.trim() || null }
      : {}),
    ...(form.apcValidUntil !== (p.apcValidUntil ?? '')
      ? { apcValidUntil: form.apcValidUntil || null }
      : {}),
  };
  const dirty = Object.keys(changed).length > 0;
  const err = save.error;
  const expired =
    !!p.apcValidUntil &&
    p.apcValidUntil < new Date().toISOString().slice(0, 10);

  if (!canEdit)
    return (
      <dl className={parity.kv} aria-label="Registration and contact">
        <dt>Email</dt>
        <dd>{p.email ?? '-'}</dd>
        <dt>MMC number</dt>
        <dd>{p.mmcNumber ?? 'Not set'}</dd>
        <dt>APC valid until</dt>
        <dd>{p.apcValidUntil ?? 'Not set'}</dd>
      </dl>
    );

  return (
    <section aria-label="Registration and contact" className={parity.form}>
      <strong>Registration and contact</strong>
      {(!p.mmcNumber || !p.apcValidUntil || expired) && (
        <p role="note">
          Without an MMC number and a current APC this doctor cannot sign
          prescriptions or issue MCs.
        </p>
      )}
      <Field label="Full name">
        <Input
          value={form.fullName}
          maxLength={160}
          onChange={set('fullName')}
        />
      </Field>
      {canEmail ? (
        <Field label="Sign-in email">
          <Input
            type="email"
            value={form.email}
            maxLength={254}
            onChange={set('email')}
          />
        </Field>
      ) : (
        <p>
          Sign-in email: {p.email ?? '-'} (only the Super admin changes it).
        </p>
      )}
      <Field
        label="MMC number"
        hint="3-20 letters, digits or hyphens."
        error={mmcBad ? 'Use letters, digits or hyphens (3-20).' : undefined}
      >
        <Input
          value={form.mmcNumber}
          maxLength={20}
          onChange={set('mmcNumber')}
        />
      </Field>
      <Field label="APC valid until">
        <Input
          type="date"
          value={form.apcValidUntil}
          onChange={set('apcValidUntil')}
        />
      </Field>
      {err ? (
        <p role="alert">
          {isApiError(err) && err.code === 'DOCTOR_DETAILS_CHANGED'
            ? 'Someone else changed these details. Close and open the doctor again.'
            : isApiError(err)
              ? (err.problem?.detail ?? err.message)
              : 'Could not save the details.'}
        </p>
      ) : null}
      <Button
        size="sm"
        disabled={!dirty || mmcBad || form.fullName.trim().length < 3}
        loading={save.isPending}
        onClick={() =>
          void save
            .mutateAsync({ ...changed, detailsVersion: p.detailsVersion })
            .then(() => toast.show({ tone: 'success', title: 'Details saved' }))
            .catch(() => undefined)
        }
      >
        Save details
      </Button>
    </section>
  );
}
